import { ReactSelectValue } from 'types/forms';

import Input from 'components/common/forms/Input';
import Select from 'components/common/forms/Select';
import Icon from 'components/common/Icon';
import Tooltip from 'components/common/Tooltip';

import { DIY, FULL_SERVICE, HYBRID } from '../SurveyTypePanel/SurveyTypePanel';
import Alert from 'components/common/Alert';

interface SampleDetailsProps {
  surveyType: string;
  numberOfRespondents: number | undefined;
  setNumberOfRespondents: (arg: number | undefined) => void;
  estimatedIncidence: ReactSelectValue<string> | null;
  setEstimatedIncidence: (arg: ReactSelectValue<string> | null) => void;
  numberOfQuestions: ReactSelectValue<string> | null;
  numberOfQuestionsOptions: ReactSelectValue<string>[] | undefined;
  setNumberOfQuestions: (arg: ReactSelectValue<string> | null) => void;
}

export const INCIDENCE_OPTIONS = [
  { label: '100 - 75%', value: '75' },
  { label: '74 - 50%', value: '50' },
  { label: '49 - 30%', value: '30' },
  { label: '29 - 20%', value: '20' },
  { label: '19 - 10%', value: '10' },
  { label: '9 - 5%', value: '5' },
  { label: '4 - 3%', value: '3' },
  { label: '2 - 1%', value: '1' },
] satisfies ReactSelectValue<string>[];

const FullServiceDesignBanner = () => (
  <Alert
    color="info"
    supportingText={
      <span className="text-[#2A4F4B]">
        Your estimate is based on respondent count, incidence rate, and survey
        length. We handle everything - strategy, design, programming, fielding,
        and delivery.
      </span>
    }
  >
    <span className="text-[#C4521E]">Full-Service Survey</span>
  </Alert>
);

const HybridDesignBanner = () => (
  <Alert
    color="info"
    supportingText={
      <span className="text-[#2A4F4B]">
        Your estimate is based on respondent count, incidence rate, and survey
        length. You manage as much design and fielding as you'd like; let us
        take care of the rest.
      </span>
    }
  >
    <span className="text-[#C4521E]">Hybrid Survey</span>
  </Alert>
);

const DIYDesignBanner = () => (
  <Alert
    color="info"
    supportingText={
      <span className="text-[#2A4F4B]">
        Your estimate is based on respondent count, incidence rate, and survey
        length. You manage design and fielding independently.
      </span>
    }
  >
    <span className="text-[#C4521E]">DIY Survey</span>
  </Alert>
);

const SampleDetailsPanel = ({
  surveyType,
  numberOfRespondents,
  setNumberOfRespondents,
  estimatedIncidence,
  setEstimatedIncidence,
  numberOfQuestions,
  numberOfQuestionsOptions,
  setNumberOfQuestions,
}: SampleDetailsProps) => {
  return (
    <div className="flex flex-grow flex-col rounded-lg shadow p-8 text-left h-fit gap-4 bg-white">
      <div>
        <p className="font-bold text-2xl">Sample details</p>
        <p className="text-gray-500">
          Tell us about your research to get an accuate estimate.
        </p>
      </div>
      <div className="flex gap-4">
        <div className="grow">
          <div className="flex gap-1 items-center">
            Number of Respondents{' '}
            <Tooltip
              trigger={
                <div className="w-4 h-4 text-gray-500 hover:text-gray-700">
                  <Icon id="info-circle" />
                </div>
              }
            >
              Enter how many people you'd like to complete your survey. This is
              the number you pay for. People who do not qualify for your survey
              do not count towards this number.
            </Tooltip>
          </div>
          <Input
            id="respondentsCount"
            min={0}
            name="respondentsCount"
            onChange={(event) => {
              setNumberOfRespondents(parseInt(event.target.value, 10));
            }}
            size="md"
            type="number"
            value={numberOfRespondents}
          />
        </div>
        <div className="grow">
          <div className="flex gap-1 items-center">Estimated Incidence</div>
          <Select
            id="incidence"
            name="incidence"
            onChange={(option) => {
              setEstimatedIncidence(option);
            }}
            options={INCIDENCE_OPTIONS}
            value={estimatedIncidence}
          />
        </div>
        <div className="grow">
          <div className="flex gap-1 items-center">
            Number of Questions{' '}
            <Tooltip
              trigger={
                <div className="w-4 h-4 text-gray-500 hover:text-gray-700">
                  <Icon id="info-circle" />
                </div>
              }
            >
              The number of questions impacts the length of the survey and
              therefore how many people who qualify will complete it. Because of
              this, longer surveys will cost more than shorter ones. But
              remember, core demographics are free and not included in your
              question count, so focus on questions unique to you.
            </Tooltip>
          </div>
          <Select
            id="questionCount"
            name="questionCount"
            onChange={(option) => {
              setNumberOfQuestions(option);
            }}
            options={numberOfQuestionsOptions ?? []}
            value={numberOfQuestions}
          />
        </div>
      </div>
      {surveyType === FULL_SERVICE && <FullServiceDesignBanner />}
      {surveyType === HYBRID && <HybridDesignBanner />}
      {surveyType === DIY && <DIYDesignBanner />}
    </div>
  );
};

export default SampleDetailsPanel;
